import type {
  AssetReference,
  ContentVersionBlob,
  OptionValue,
  PostComponent,
  PostOption,
} from '@opallabs/api/v3'
import { shouldShowContent } from '@opallabs/periodic'
import type { DerivedPostComponent, TranslatorInputs } from './types'

const deriveOptionValues = (postOptions: PostOption[]): OptionValue[] =>
  postOptions
    .map((option) => option.option_value)
    .filter((value): value is OptionValue => Boolean(value))

const derivePostComponents = (
  postComponents: PostComponent[]
): DerivedPostComponent[] =>
  postComponents.map((component) => ({
    id: component.id,
    component_id: component.component?.id ?? component.component_id,
    position: component.position,
    component_name:
      component.component?.system_name ?? component.component_name,
  }))

const deriveCoverAssets = ({ post }: ContentVersionBlob): AssetReference[] => {
  const fromAssetReferencePosts = (post.asset_reference_posts ?? []).map(
    (entry) => entry.asset_reference?.cover_asset_reference
  )
  const fromOptionAssets = post.post_options.map(
    (option) => option.asset_reference?.cover_asset_reference
  )

  return [...fromAssetReferencePosts, ...fromOptionAssets].filter(
    (asset): asset is AssetReference => Boolean(asset)
  )
}

/**
 * Reconstructs the argument object `Periodic.BackbonePostTranslator` expects from a
 * captured version blob. Mirrors the live Backbone preview derivation in
 * `periodic_preview.coffee` (`_getOptionValueJson`, `_getPostComponentJson`,
 * `_getCoverAssetsJson`, `_getLinksWithDataJson`), reading plain serialized JSON
 * rather than Backbone models. Option values nest inside `post_options` in the blob
 * (`PostOptionSerializer has_one :option_value`), so they're plucked back out here.
 */
export const deriveTranslatorInputs = (
  blob: ContentVersionBlob
): TranslatorInputs => {
  const { post, account } = blob

  return {
    postJson: post,
    linksWithDataJson: post.links_with_data ?? [],
    accountJson: account ?? {},
    optionValueJson: deriveOptionValues(post.post_options),
    postComponentsJson: derivePostComponents(post.post_components ?? []),
    coverAssetsJson: deriveCoverAssets(blob),
  }
}

/** The service system name for a version, e.g. `"instagram"`. */
export const getServiceSystemName = (blob: ContentVersionBlob): string =>
  blob.post.service?.system_name ?? blob.account?.service?.system_name ?? ''

/**
 * Whether a version can render through the live Periodic preview. Unrenderable
 * service/type combinations fall back to a plain-text card.
 */
export const isPeriodicRenderable = (blob: ContentVersionBlob): boolean =>
  shouldShowContent(getServiceSystemName(blob), blob.post.post_type)

/**
 * Section components whose renderers hide their sections behind carousel
 * navigation — the `ModularCarousel` consumers in Periodic. Sections of other
 * kinds (a Twitter thread's tweets, say) are all on screen in the standard
 * preview, so their coordinates need no special treatment.
 */
const CAROUSEL_SECTION_COMPONENTS = new Set([
  'fb_story_carousel_section',
  'ig_car_ad_section',
  'ig_car_app_ad_section',
  'ig_car_section',
  'ig_expandable_story_link_ad_section',
  'ig_native_story_link_ad_section',
  'ig_story_section',
  'tiktok_carousel_section',
])

/**
 * Whether the preview shows one section at a time, hiding the rest. Pins on such
 * content are only meaningful on the expanded layout, where every section is
 * stacked and every pixel belongs to exactly one of them.
 */
export const hasHiddenSections = (blob: ContentVersionBlob): boolean =>
  (blob.post.post_components ?? []).filter((component) =>
    CAROUSEL_SECTION_COMPONENTS.has(
      component.component?.system_name ?? component.component_name ?? ''
    )
  ).length > 1

export type VersionCopy = { title: string; body: string }

/** Copy fields shown when a version can't render through the Periodic preview. */
export const getVersionCopy = (blob: ContentVersionBlob): VersionCopy => ({
  title: blob.post.title ?? blob.post.name ?? '',
  body: blob.post.content ?? blob.post.long_description ?? '',
})

/** One-line preview shown for a version in the rail. */
export const getVersionPreviewText = (blob: ContentVersionBlob): string =>
  blob.post.content ?? blob.post.title ?? blob.post.name ?? ''
